/*
 * Copyright (c) 2022 Puratos. All rights reserved.
 */

import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { useI18N } from '../../my-puratos/hoc/withI18N';
import { useDispatch, useSelector } from 'react-redux';
import { requestPrice } from '../../../actions/my-puratos/requestPrice.action';
import { WithConfirmation } from '../../hoc/withConfirmation';
import {
    selectLoading,
    selectRequestedPrices,
} from '../../../selectors/my-puratos/requestPrice';
import { getHomepage, getLanguageIso, isMyPuratosEnabled } from '../../../selectors/generic';
import { getActiveSku } from '../../../selectors/productPage';
import { googleEvents } from '../../../utils/googleEvents';
import { IPackaging } from '../../../interfaces/my-puratos/IGeneric';
import { GTMContext } from '../../../analytics/ga4/utils/GTMContext';
import { interactionListMapper, interactionPageMapper } from '../../../analytics/ga4/utils/mapper';
import { formatStringNumber } from '../../../analytics/ga4/utils/helpers';

interface IProps {
    productId: number;
    text?: string;
    productCategoryTag?: string;
    productRowData?: IPackaging;
}

interface GoogleDataProps {
    item_brand: string;
    item_id: string;
    item_list_name: string;
    item_name: string;
    item_variant: string;
    login_status: string;
    page_language: string;
    website_section: string;
}

export const RequestPriceButton: FunctionComponent<IProps> = ({
    productId,
    text = 'Request a price',
    productCategoryTag,
    productRowData,
}) => {
    const translate = useI18N();
    const dispatch = useDispatch();
    const [response, setResponse] = useState(false);
    const [googleData, setGoogleData] = useState<GoogleDataProps>();
    const [buttonAnimationActive, setButtonAnimationActive] = useState(false);
    const loading = useSelector(selectLoading);
    const requestedPricesState = useSelector(selectRequestedPrices);
    const homepage = useSelector(getHomepage);
    const activeProduct = useSelector(getActiveSku);
    const isMyAccountEnabled = useSelector(isMyPuratosEnabled);

    const contextType = useContext(GTMContext);

    const currentProduct = useMemo(() => {
        localStorage.setItem(
            'requestedPrices',
            JSON.stringify(requestedPricesState.requestedPrices)
        );
        return requestedPricesState.requestedPrices.filter(
            (object) => object.productId === productId
        );
    }, [requestedPricesState]);

    const isProductRequested = useMemo(() => {
        return currentProduct.length > 0;
    }, [currentProduct]);

    const afterOsCall = () => {
        setResponse(true);
    };

    const buildAndSendGoogleData = () => {
        try {
            const language = getLanguageIso();
            const {
                aemProdName,
                materialNumber,
                salesUnit,
                baseUnit,
                packaging
            } = productRowData || activeProduct  || {};
            // productCategoryTag e.g. "belgium:products/categories/chocolade/Specialiteit"
            const categoryStringArray = productCategoryTag?.split('products/categories/');
            const categoryString = categoryStringArray?.length > 1 ? categoryStringArray[1] : '';

            const categoryArray = categoryString?.split('/');

            const item_category = categoryArray?.length > 0 ? categoryArray[0] : '';
            const item_category2 = categoryArray?.length > 1 ? categoryArray[1] : '';

            const item_list_name = interactionListMapper(contextType) ?? '';
            const website_section = interactionPageMapper(contextType) ?? '';

            const dataToSend = {
                item_brand: 'Puratos',
                item_id: materialNumber?.toString(),
                item_list_name,
                item_name: aemProdName ?? '',
                item_variant: salesUnit
                ? `${salesUnit} ${formatStringNumber(packaging)} ${
                      baseUnit
                  }`
                : packaging,
                login_status: isMyAccountEnabled ? 'logged_in' : 'guest',
                page_language: language,
                website_section,
                item_category,
                item_category2
            };

            return dataToSend;

        } catch (e) {
            console.warn('Analytics error', e);
        }
    };

    useEffect(() => {
        const googleData = buildAndSendGoogleData();

        setGoogleData(googleData);
    }, [activeProduct]);

    useEffect(() => {
        if (loading) {
            setButtonAnimationActive(true);
        }

        let fxTimeout = null;
        if (!loading && buttonAnimationActive) {
            fxTimeout = setTimeout(() => setButtonAnimationActive(false), 3000);
        }

        return () => clearTimeout(fxTimeout);
    }, [loading]);

    const onClick = () => {
        dispatch(requestPrice(productId, afterOsCall));
        googleEvents.priceRequest(googleData);
    };

    return (
        <a
            href="#"
            className={`btn-request-price btn-primary ${isProductRequested && !loading ? 'disabled' : ''
                } ${buttonAnimationActive ? ' button-animation-active' : ''}`}
            onClick={onClick}
        >
            <WithConfirmation
                confirmation={loading}
                response={response}
                longButton
            >
                {translate(
                    isProductRequested && !loading
                        ? 'Price requested'
                        : homepage.extraInformationButtonText || text
                )}
            </WithConfirmation>
        </a>
    );
};
